import JSZip from "jszip";
import {
  buildCheckVoucherPrintHtml,
  buildCheckVoucherRequestHtml,
} from "./checkVoucherHtml";

const sanitizePathPart = (value, fallback) => {
  const text = String(value || "").trim() || fallback;
  return text.replace(/[<>:"/\\|?*]+/g, "_").replace(/\s+/g, " ").trim().slice(0, 120);
};

const getExtensionFromType = (type = "") => {
  const normalized = String(type).toLowerCase();
  if (normalized.includes("pdf")) return ".pdf";
  if (normalized.includes("png")) return ".png";
  if (normalized.includes("jpeg")) return ".jpg";
  if (normalized.includes("jpg")) return ".jpg";
  if (normalized.includes("webp")) return ".webp";
  if (normalized.includes("gif")) return ".gif";
  if (normalized.includes("sheet")) return ".xlsx";
  if (normalized.includes("excel")) return ".xls";
  if (normalized.includes("word")) return ".docx";
  if (normalized.includes("csv")) return ".csv";
  if (normalized.includes("zip")) return ".zip";
  return "";
};

const getAttachmentFileName = (doc, index) => {
  const provided = String(doc?.file_name || "").trim();
  if (provided) return sanitizePathPart(provided, `attachment-${index + 1}`);
  const ext = getExtensionFromType(doc?.file_type);
  return `${sanitizePathPart(doc?.id || `attachment-${index + 1}`, `attachment-${index + 1}`)}${ext}`;
};

const triggerBlobDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadCheckVoucherAttachmentsZip = async ({ voucher, voucherNo, requests = [], lines = [] }) => {
  const requestsWithAttachments = requests.filter(
    (request) => Array.isArray(request.attachments) && request.attachments.length > 0
  );

  const zip = new JSZip();
  const baseName = sanitizePathPart(voucherNo || voucher?.voucher_no || "check-voucher", "check-voucher");

  zip.file(
    `${baseName}-check-voucher.html`,
    buildCheckVoucherPrintHtml({
      voucher: voucher || { voucher_no: voucherNo },
      requests,
      lines,
    })
  );

  zip.file(
    `${baseName}-request-details.html`,
    buildCheckVoucherRequestHtml({
      requests,
    })
  );

  if (voucher?.for_soa && voucher?.soa_file_url) {
    const response = await fetch(voucher.soa_file_url);
    if (!response.ok) throw new Error("Failed to download the SOA attachment");
    zip.file(
      `SOA/${sanitizePathPart(voucher.soa_file_name || "statement-of-account", "statement-of-account")}`,
      await response.blob()
    );
  }

  for (const request of requestsWithAttachments) {
    const folderName = sanitizePathPart(request.rfp_no || request.job_order || "request", "request");
    const requestFolder = zip.folder(folderName);

    for (let index = 0; index < request.attachments.length; index += 1) {
      const doc = request.attachments[index];
      const fileUrl = doc?.file_url || doc?.file_path;
      if (!fileUrl) continue;

      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to download attachment: ${doc.file_name || "Attachment"}`);
      }

      const blob = await response.blob();
      requestFolder.file(getAttachmentFileName(doc, index), blob);
    }
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  triggerBlobDownload(zipBlob, `${baseName}-packet.zip`);
};
